import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    signal,
    ViewChild,
    ViewEncapsulation,
    output,
    Input,
    effect,
    Signal,
} from '@angular/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { Emoji } from '@ctrl/ngx-emoji-mart/ngx-emoji/emoji.component';
import { IReactionData, IReactionFavoriteModel, IReactionModel, IEmojiViewData } from '@fi-shared/fi-likes/fi-likes.d';
import { MatMenu, MatMenuTrigger, MenuPositionX, MenuPositionY } from '@angular/material/menu';
import { FiLikesStoreService } from '../services/fi-likes-store.service';
import { FiLikesFactoryService } from '../services/fi-likes-factory.service';

@Component({
    selector: 'fi-reaction-picker',
    templateUrl: './fi-reaction-picker.component.html',
    styleUrls: ['./fi-reaction-picker.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'fi-reaction-picker',
        '[class.visible]': 'isReactionPanelOpen() || isPickerButtonVisible() || isEmojiMartReactionPanelOpen()',
    },
})
export class FiReactionPickerComponent implements OnInit {
    public isPickerButtonVisible = input<boolean>(false);
    public reactions = input<IReactionModel[]>([]);
    public isEmojiMartReactionPanelOpen = input<boolean>(false);
    @Input({ required: true }) containerEl: HTMLElement;

    public toggleReactionEmojiMartVisibility = output<void>();
    public onReactionSelect = output<IReactionData>();
    public onReactionRemove = output<IReactionModel>();
    public onReactionReplace = output<{ reaction: IReactionModel; newEmoji: IEmojiViewData }>();

    private readonly fiUserDataService = inject(FiUserDataService);
    private readonly fiEmojiService = inject(FiEmojiService);
    private readonly fiLikesStoreService = inject(FiLikesStoreService);
    private readonly fiLikesFactoryService = inject(FiLikesFactoryService);
    private readonly elRef = inject(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    protected isReactionPanelOpen = signal<boolean>(false);

    private readonly panelMaxWidth = 216;
    private readonly panelHeight = 40;

    protected menuYPosition = signal<MenuPositionY>('above');
    protected menuXPosition = signal<MenuPositionX>('after');

    protected get favoriteReactions(): Signal<IReactionFavoriteModel[]> {
        return this.fiLikesStoreService.favoriteReactions;
    }

    protected readonly currentUserId: number = this.fiUserDataService.getUserId();

    @ViewChild('reactionsMenuTrigger', { read: ElementRef }) emojiPickerButtonRef: ElementRef<HTMLButtonElement>;
    @ViewChild('reactionPickerMenu') reactionPickerMenu: MatMenu;

    constructor() {
        effect(() => {
            if (!this.isEmojiMartReactionPanelOpen()) {
                this.fiEmojiService.setEmojiOverlayVisibility({ isEmojiOverlayShown: false });
            }
        });
    }

    public ngOnInit(): void {
        this.fiEmojiService
            .onEmojiInsertion$()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.isEmojiMartReactionPanelOpen()),
            )
            .subscribe((emoji) => {
                const reactionData = this.fiLikesFactoryService.getReactionDataFromEmoji({
                    id: emoji.id,
                    set: emoji.set as Emoji['set'],
                    sheet: emoji.sheet,
                    unified: emoji.unified,
                });
                this.onEmojiSelect(reactionData);
                this.fiEmojiService.setEmojiOverlayVisibility({ isEmojiOverlayShown: false });
            });
    }

    protected openEmojiMart(): void {
        this.toggleReactionEmojiMartVisibility.emit();
        this.fiEmojiService.setEmojiOverlayVisibility({
            isEmojiOverlayShown: true,
            triggerElement: this.emojiPickerButtonRef,
        });
    }

    protected onEmojiSelect(reactionData: IReactionData): void {
        const currentUserReaction = this.reactions()?.find((reaction) => reaction.UserId === this.currentUserId);
        if (this.isCurrentUserReaction(reactionData)) {
            this.onReactionRemove.emit(currentUserReaction);
            return;
        }
        this.onReactionSelect.emit(reactionData);
    }

    protected openReactionPickerMenu(menuTrigger: MatMenuTrigger): void {
        this.setMenuPosition();
        menuTrigger.openMenu();
    }

    private setMenuPosition(): void {
        const containerElRect = this.containerEl?.getBoundingClientRect();
        const elRect = this.elRef.nativeElement.getBoundingClientRect();
        if (!containerElRect || !elRect || !this.reactionPickerMenu) {
            return;
        }

        const elLeftOffsetFromContainer = elRect.left - containerElRect.left;
        const xPosition: MenuPositionX = elLeftOffsetFromContainer >= this.panelMaxWidth ? 'before' : 'after';
        const elTopOffsetFromContainer = elRect.top - containerElRect.top;
        const yPosition: MenuPositionY = elTopOffsetFromContainer >= this.panelHeight ? 'above' : 'below';

        this.menuXPosition.set(xPosition);
        this.menuYPosition.set(yPosition);
        // MatMenu reads position when opening; template bindings may not have flushed yet on first open.
        this.reactionPickerMenu.xPosition = xPosition;
        this.reactionPickerMenu.yPosition = yPosition;
    }

    protected isCurrentUserReaction(reactionData: IReactionData): boolean {
        return !!this.reactions()?.find(
            (reaction) =>
                reaction.UserId === this.currentUserId &&
                this.fiLikesFactoryService.isReactionDataEquals(reaction.Data, reactionData),
        );
    }
}
